<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import logger from '@/logger';
import {
  getSidebarByString,
  initSidebar,
  parseSidebarMeta,
  SIDEBAR,
} from './sidebar';
import type { SidebarMeta } from './sidebar';
import { DATA_QA } from './sidebar-wrapper.constants';
import NotImplementedSidebar from './NotImplementedSidebar.vue';
import PhoneSidebar from '@/phone/PhoneSidebar/PhoneSidebar.vue';
import PhoneEdit from '@/phone/PhoneEdit/PhoneEdit.vue';

const log = logger.getChild('SidebarWrapper');

log.debug('Mounting');
onMounted(() => {
  log.debug('Mounted');
});

const router = useRouter();
initSidebar(router);

const sidebar = ref<SIDEBAR | null>(null);
const meta = ref<SidebarMeta | null>(null);

watch(
  router.currentRoute,
  // eslint-disable-next-line max-statements
  (newValue, oldValue) => {
    if (
      oldValue?.query.sidebar !== newValue.query.sidebar ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      oldValue?.query.sidebarMeta !== newValue.query.sidebarMeta
    ) {
      const sidebarFromQuery = newValue.query.sidebar;
      const metaFromQuery = newValue.query.sidebarMeta;
      if (!sidebarFromQuery || Array.isArray(sidebarFromQuery)) {
        log.debug('No sidebar in query. Closing sidebar');
        sidebar.value = null;
        meta.value = null;
        return;
      }

      const parsedMeta = parseSidebarMeta(metaFromQuery);
      if (metaFromQuery && !parsedMeta) {
        log.error(`Invalid sidebar meta value: ${metaFromQuery}`);
        sidebar.value = null;
        meta.value = null;
        return;
      }
      const sidebarToOpen = getSidebarByString(sidebarFromQuery);
      if (!sidebarToOpen) {
        log.error(`Invalid sidebar value: ${sidebarFromQuery}`);
      }

      log.debug('Opening sidebar', sidebarToOpen, parsedMeta);
      sidebar.value = sidebarToOpen;
      meta.value = parsedMeta;
    }
  },
  { immediate: true },
);

const resizableContainerRef = ref<HTMLElement | null>(null);
const containerWidth = ref(384);

const isResizing = ref(false);
const initialMouseX = ref(0);
const initialContainerWidth = ref(0);

const containerStyle = computed(() => ({
  width: `${containerWidth.value}px`,
}));

const handleMouseDown = (e: MouseEvent) => {
  isResizing.value = true;
  initialMouseX.value = e.clientX;
  initialContainerWidth.value = resizableContainerRef.value?.offsetWidth ?? 0;

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);

  document.body.style.userSelect = 'none';
  document.body.style.cursor = 'ew-resize';
};

const handleMouseMove = (e: MouseEvent) => {
  if (!isResizing.value) return;

  const deltaX = e.clientX - initialMouseX.value;
  let newWidth = initialContainerWidth.value - deltaX;

  const minWidth = 264;
  const maxWidth = 784;

  if (newWidth < minWidth) {
    newWidth = minWidth;
  } else if (newWidth > maxWidth) {
    newWidth = maxWidth;
  }

  containerWidth.value = newWidth;
};

const handleMouseUp = () => {
  isResizing.value = false;

  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);

  document.body.style.userSelect = '';
  document.body.style.cursor = '';
};

onUnmounted(() => {
  if (isResizing.value) {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }
});
</script>
<template>
  <div
    v-if="sidebar"
    ref="resizableContainerRef"
    :data-qa="DATA_QA.ROOT"
    class="d-h100p d-d-flex d-bl d-bc-default"
    :style="containerStyle"
  >
    <div
      :class="`d-h100p d-c-col-resize d-mln4 d-w8 d-ps-relative sidebar-wrapper-resize ${
        isResizing ? 'sidebar-wrapper-resize--resizing' : ''
      }`"
      @mousedown="handleMouseDown"
    ></div>
    <div class="d-fl-shrink1 d-fl-grow1">
      <PhoneSidebar
        v-if="sidebar === SIDEBAR.PHONE"
        :id="meta?.id"
        :tab="meta?.tab"
      />
      <PhoneEdit v-if="sidebar === SIDEBAR.PHONE_EDIT" :e164="meta?.id" />
      <NotImplementedSidebar v-else-if="sidebar === SIDEBAR.NOT_IMPLEMENTED" />
    </div>
  </div>
</template>
<style>
.sidebar-wrapper-resize {
  width: 10px;
  transition: background-color 0.15s 0.15s, box-shadow 0.15s 0.15s;
}

.sidebar-wrapper-resize:hover {
  background-color: color-mix(
    in srgb,
    var(--dt-color-border-focus) 50%,
    transparent
  );
}

.sidebar-wrapper-resize--resizing::after {
  content: '';
  position: absolute;
  left: 1px;
  top: 0;
  width: 5px;
  height: 100%;
  background-color: color-mix(
    in srgb,
    var(--dt-color-border-focus) 50%,
    transparent
  );
}
</style>
